<template>
  <Alert
    v-if="showAlert"
    :message="alertMessage"
    :alertType="alertType"
    :closable="alertClosable"
    :alertIcon="alertIcon"
    @onClose="showAlert = false"
  />
  <div class="container p-3" style="gap: 10px">
    <div class="w-100 text-right" @click="closeBack">
      <img :src="getIcon('img/credit-line/close_icon.svg')" alt="icon" />
    </div>
    <img
      :src="getIcon('img/credit-line/delete_virtual_card.svg')"
      alt="icon"
      class="mt-3 mb-2 w-50"
    />

    <p
      class="mb-2 fw-bold fs-5 text-center text-black"
      v-html="t('PAYJOY_CARD_VIEWS.CC_CONFIRM_DELETE_VIRTUAL_CARD_TITLE')"
    ></p>
    <p
      class="mb-2 fs-6 text-center text-grey"
      v-html="t('PAYJOY_CARD_VIEWS.CC_CONFIRM_DELETE_VIRTUAL_CARD_TEXT')"
    ></p>
    <div
      style="margin-top: 5rem"
      class="floating-btn w-100 d-flex flex-column gap-0"
    >
      <p
        class="text-center p-1 mb-2 text-success fw-bold keep-card"
        @click="closeBack()"
      >
        {{ t("PAYJOY_CARD_VIEWS.CC_CONFIRM_DELETE_VIRTUAL_CARD_NO_KEEP") }}
      </p>
      <Button
        id="btn-continue"
        class="text-start w-100 p-2 mb-0"
        @click="deleteCard()"
      >
        <p class="text-center text-white fw-bold my-auto">
          {{ t("PAYJOY_CARD_VIEWS.CC_CONFIRM_DELETE_VIRTUAL_CARD_BUTTON") }}
        </p>
      </Button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import Button from "@/apps/credit-line/components/Button.vue";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import { useRouter, useRoute } from "vue-router";
import Alert from "@/shared-components/common/Alert.vue";
import { ErrorHandler } from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import { useStore } from "vuex";
declare const Android: AndroidInterface;

export default defineComponent({
  name: "ConfirmDeleteVirtual",
  components: { Button, Alert },
  emits: ["dismissFunc"],
  methods: {
    getIcon(path: string): string {
      return useAsset(path);
    },
  },
  computed: {
    DeviceTag() {
      return AuthenticationHelper.getDeviceTag();
    },
  },
  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const route = useRoute();

    const alertMessage = ref<string>();
    const showAlert = ref<boolean>(false);
    const alertIcon = ref<string>("fa fa-exclamation-triangle");
    const alertType = ref<string>("alert-danger");
    const alertClosable = ref<boolean>(true);

    const lastFour = route.query.lastFour as string;

    const closeBack = () => {
      router.back();
    };

    const store = useStore();

    const deleteCard = async () => {
      try {
        await store.dispatch("creditLine/deleteVirtualCard", lastFour);
        Android.deleteCardFromWallet(lastFour);

        router.push({
          name: "CreditCardDetailsView",
          query: { defaultType: "VIRTUAL" },
        });
      } catch (error) {
        alertMessage.value = t("CARDSTATUSTOGGLEERROR");
        showAlert.value = true;
        ErrorHandler.handleNetworkErrorAndLog(error);
      }
    };

    return {
      t,
      route,
      router,
      showAlert,
      alertClosable,
      alertIcon,
      alertMessage,
      alertType,
      deleteCard,
      closeBack,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/constants";
$pj-color-red-opaque: rgba(222, 14, 14, 0.4);
.error-icon-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  color: $pj-color-white;
  width: 60px;
  height: 60px;
  border-radius: 30px;
  background-color: $pj-color-red;
  border: 5px solid $pj-color-red-opaque;
  -webkit-background-clip: padding-box; /* for Safari */
  background-clip: padding-box; /* for IE9+, Firefox 4+, Opera, Chrome */
  font-size: 40px;

  i {
    margin: 0;
  }
}

.w-50 {
  width: 50%;
}

.btn-outline-success {
  background-color: transparent !important;
  border: 1px solid #2c7040 !important;
}

.container {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 16px;
  flex: 1 0 0;
  align-self: stretch;
}

#btn-gotohome {
  border-radius: 12px;
  height: 3rem;
  width: 100%;
  z-index: 500;
}

#btn-gotohome p {
  font-size: large;
}

.floating-btn {
  left: 0;
  bottom: 0;
  z-index: 200;
}

#device-tag {
  color: var(--major-disable, #828282);
  text-align: center;
  font-feature-settings: "clig" off, "liga" off;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 19.6px; /* 140% */
  letter-spacing: 0.21px;
}

.keep-card {
  cursor: pointer;
  color: #2c7040;
  border: none;
  background: transparent;
}
</style>
